<script lang="ts" setup>
import buildProductUrl from '@/utils/routing/buildProductUrl'
import getDiscount from '@/utils/getDiscount'
import ProductImg from '~/components/Product/ProductImg.vue'
import Sticker from '~/components/Product/Sticker.vue'
import ProductCardPrice from '~/components/Product/ProductCardPrice.vue'
import type { CarouselProduct } from '~/types/Product'

const props = defineProps<CarouselProduct>()
const { t } = useLang()
const { isAlt } = useTheme()
const discount = computed(() => getDiscount(props.current_price, props.old_price))

const texts = computed(() => {
  return {
    view: t('common.view'),
  }
})
</script>

<template>
  <div class="product-slider-card relative flex flex-col p-2 lg:p-4 alt:!rounded-none">
    <NuxtLink v-if="quote_slug" class="product-link absolute" :to="buildProductUrl(quote_slug, id)" />
    <a v-else class="product-link absolute" :href="link" />
    <div class="img-wrapper flex items-center relative">
      <ProductImg
        v-if="image"
        :src="image"
        class="product-img"
        size="md"
        :alt="`${props.manufacturer} ${props.name}`" />
    </div>
    <div class="dark:p-4 absolute top-4 right-4">
      <PromoSticker :enabled="props.sticker_enabled" :src="props.sticker_img" is-slider-card />
    </div>
    <div class="sticker-wrapper absolute top-4 left-4 dark:top-2 dark:left-2">
      <Sticker v-if="discount">
        {{ discount }}
      </Sticker>
    </div>
    <div class="product-details flex flex-col flex-1">
      <span class="manufacturer text-text2 dark:text-dmText2 min-h-5 line-clamp-1">{{ manufacturer }}</span>
      <div class="title text-text1 dark:text-dmText1 overflow-hidden h-[2.5rem] lg:text-lg lg:h-[2.75rem]">
        {{ name }}
      </div>
      <ProductCardPrice :old-price="old_price" :current-price="current_price" />
      <template v-if="!isAlt">
        <NuxtLink v-if="quote_slug" class="btn-secondary mt-2" :to="buildProductUrl(quote_slug, id)">
          {{ texts.view }}
        </NuxtLink>
        <a v-else class="btn-secondary mt-2" :href="link">{{ texts.view }}</a>
      </template>
    </div>
  </div>
</template>

<style lang="postcss">
.product-slider-card {
  @apply bg-white dark:bg-dmGray1 dark:outline-none;
  @apply outline outline-1 -outline-offset-1 outline-gray2 rounded-sm;

  height: 100%;

  .product-link {
    top: 0;
    left: 0;
    z-index: 2;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .img-wrapper {
    height: 311px;
    background: white;
    border-radius: theme('vars.borderRadiusLight');
    overflow: hidden;

    .product-img {
      margin: 0 auto;
      object-fit: contain;
      max-width: 100%;
      max-height: 100%;
      width: 100%;
      height: 100%;
    }
  }

  .product-details {
    padding-top: 0.625rem;

    .manufacturer {
      color: theme('colors.gray6');
      font-weight: 600;
    }

    .title {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow-wrap: break-word;
      word-break: break-word;
    }

    a {
      z-index: 2;
    }
  }

  &:hover {
    @apply shadow-lg dark:shadow-dmGray4;
    transition: all 0.2s ease-out;
  }

  .btn-secondary {
    display: none;
  }

  @screen lg {
    .btn-secondary {
      display: flex;
    }

    .img-wrapper img {
      width: 100%;
    }
  }
}
</style>

<style lang="postcss" scoped>
.pj {
  .product-slider-card {
    @apply bg-white dark:bg-dmGray2;

    .manufacturer {
      @apply uppercase pb-2 text-red;
    }
  }
}

.wl {
  .product-slider-card {
    @apply bg-white dark:bg-dmGray1;

    .img-wrapper {
      @apply rounded-sm border border-neutral5 bg-neutral7 p-4;

      img {
        @apply mix-blend-multiply;
      }
    }

    .sticker-wrapper {
      @apply top-4 left-4;
    }
  }
}

.tp {
  .product-slider-card {
    @apply rounded-none p-2 hover:z-[1];

    .manufacturer {
      @apply uppercase pb-2 text-brand-primary font-bold;
    }
  }
}
</style>
